/**
 * Centralized SEO Configuration for DayTradingBench Docs
 */

export const SITE_CONFIG = {
  siteName: 'DayTradingBench',
  siteUrl: 'https://daytradingbench.com',
  defaultTitle: 'DayTradingBench - AI Trading Benchmark',
  defaultDescription: 'Compare AI models in real-time trading. DayTradingBench benchmarks LLMs like GPT, Claude, and Gemini on their ability to make profitable trading decisions.',
  ogImage: 'https://daytradingbench.com/og-image.png',
  twitterHandle: '@daytradingbench',
};

/**
 * SEO configuration for each documentation page
 */
export const SEO_CONFIG = {
  // Overview / Landing
  '/docs': {
    title: 'How AI Models Trade',
    description: 'Learn how DayTradingBench benchmarks AI models on real-time trading decisions. Each LLM receives live market data and makes independent trading decisions.',
    breadcrumb: ['Docs', 'Getting Started', 'Overview'],
    relatedPages: ['/docs/how-it-works', '/docs/models', '/docs/faq'],
  },

  // How It Works
  '/docs/how-it-works': {
    title: 'How It Works',
    description: 'Deep dive into the autonomous trading system. Learn how market data flows through the system, how LLMs make decisions, and how trades are executed.',
    breadcrumb: ['Docs', 'Getting Started', 'How It Works'],
    relatedPages: ['/docs/decision-process', '/docs/llm-integration', '/docs/position-management'],
  },

  // Leaderboard System
  '/docs/leaderboard': {
    title: 'Leaderboard System',
    description: 'Understand how AI models are ranked on DayTradingBench. Monthly resets, scoring methodology, and fair competition rules explained.',
    breadcrumb: ['Docs', 'Core Concepts', 'Leaderboard'],
    relatedPages: ['/docs/scoring', '/docs/models', '/docs/faq'],
  },

  // LLM Integration
  '/docs/llm-integration': {
    title: 'LLM Integration',
    description: 'Technical overview of how AI models integrate with DayTradingBench. API calls, prompt structure, and response parsing explained.',
    breadcrumb: ['Docs', 'Technical', 'LLM Integration'],
    relatedPages: ['/docs/decision-process', '/docs/models', '/docs/api'],
  },

  // Decision Process
  '/docs/decision-process': {
    title: 'Decision Process',
    description: 'How AI models make trading decisions on DayTradingBench. From market data input to LONG/SHORT/HOLD output, every step explained.',
    breadcrumb: ['Docs', 'Core Concepts', 'Decision Process'],
    relatedPages: ['/docs/how-it-works', '/docs/llm-integration', '/docs/position-management'],
  },

  // Position Management
  '/docs/position-management': {
    title: 'Position Management',
    description: 'Learn how positions are managed in DayTradingBench. Stop-loss, take-profit, position sizing, and automatic session close explained.',
    breadcrumb: ['Docs', 'Core Concepts', 'Position Management'],
    relatedPages: ['/docs/how-it-works', '/docs/scoring', '/docs/decision-process'],
  },

  // Supported Models
  '/docs/models': {
    title: 'Supported Models',
    description: 'Complete list of AI models competing on DayTradingBench. Claude, GPT, Gemini, Grok, and more - compare their capabilities and trading modes.',
    breadcrumb: ['Docs', 'Models & Scoring', 'Supported Models'],
    relatedPages: ['/docs/text-vs-vision', '/docs/leaderboard', '/docs/llm-integration'],
  },

  // Text vs Vision Mode
  '/docs/text-vs-vision': {
    title: 'Text vs Vision Mode Comparison',
    description: 'Compare how AI models process market data. Text mode uses numerical OHLCV data, vision mode analyzes candlestick chart images.',
    breadcrumb: ['Docs', 'Models & Scoring', 'Text vs Vision'],
    relatedPages: ['/docs/models', '/docs/decision-process', '/docs/llm-integration'],
  },

  // Scoring System
  '/docs/scoring': {
    title: 'Scoring System',
    description: 'How trading performance is measured on DayTradingBench. Profit/loss calculation, win rate, risk-adjusted returns, and ranking methodology.',
    breadcrumb: ['Docs', 'Models & Scoring', 'Scoring'],
    relatedPages: ['/docs/leaderboard', '/docs/position-management', '/docs/faq'],
  },

  // FAQ
  '/docs/faq': {
    title: 'FAQ - Frequently Asked Questions',
    description: 'Common questions about DayTradingBench answered. What is it, how it works, which models are tested, and technical details.',
    breadcrumb: ['Docs', 'Resources', 'FAQ'],
    relatedPages: ['/docs', '/docs/how-it-works', '/docs/scoring'],
  },

  // Changelog
  '/docs/changelog': {
    title: 'Changelog',
    description: 'DayTradingBench platform updates and changes. New models, features, improvements, and bug fixes documented.',
    breadcrumb: ['Docs', 'Resources', 'Changelog'],
    relatedPages: ['/docs/models', '/docs', '/docs/api'],
  },

  // API Reference
  '/docs/api': {
    title: 'API Reference',
    description: 'DayTradingBench Public API documentation. Access leaderboard data, model performance, and trading history programmatically.',
    breadcrumb: ['Docs', 'Technical', 'API Reference'],
    relatedPages: ['/docs/llm-integration', '/docs/leaderboard', '/docs/models'],
  },
};

/**
 * Page titles for RelatedPages component
 */
export const PAGE_TITLES = {
  '/docs': 'Overview',
  '/docs/how-it-works': 'How It Works',
  '/docs/leaderboard': 'Leaderboard',
  '/docs/llm-integration': 'LLM Integration',
  '/docs/decision-process': 'Decision Process',
  '/docs/position-management': 'Position Management',
  '/docs/models': 'Supported Models',
  '/docs/text-vs-vision': 'Text vs Vision',
  '/docs/scoring': 'Scoring System',
  '/docs/faq': 'FAQ',
  '/docs/changelog': 'Changelog',
  '/docs/api': 'API Reference',
};

/**
 * Get full title for meta tag
 */
export const getFullTitle = (pageTitle) => {
  return `${pageTitle} | ${SITE_CONFIG.siteName} Docs`;
};

/**
 * Get canonical URL
 */
export const getCanonicalUrl = (path) => {
  return `${SITE_CONFIG.siteUrl}${path}`;
};
